import { Component, ErrorInfo, ReactNode } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { Alert, AlertTitle } from '@material-ui/lab';

import { captureReactException } from '@sentry/react';

export interface ErrorBoundaryProps {
  onDidCatch?: (message: string) => void;
  passiveGuard?: boolean;
  children?: ReactNode | JSX.Element;
  renderFallback?: (props: {
    message: string;
    error?: Error;
  }) => ReactNode | JSX.Element | null | undefined;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

const Fallback = ({ message, stack }: { message: string; stack?: string }) => (
  <Alert severity="error">
    <AlertTitle>Oops, something went wrong...</AlertTitle>
    <Typography variant="body2">
      Sorry, looks like we encountered an error. Try refreshing the page, or{' '}
      contact our{' '}
      <strong>
        <Link
          color="inherit"
          underline="always"
          href="mailto:experimentation-cs@spotify.com"
         data-track-source-anchor="22c4b4d358">
          Customer Support team
        </Link>
      </strong>{' '}
      for help.
    </Typography>
    <Box mt={2}>
      <Accordion elevation={0}>
        <AccordionSummary>Error details</AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="caption" paragraph>
              <strong>{message}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {stack}
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  </Alert>
);

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(e: Error, errorInfo: ErrorInfo) {
    captureReactException(e, errorInfo);
    this.props.onDidCatch?.(e.message || 'Something went wrong');

    if (this.props.passiveGuard) {
      throw e;
    }
  }

  render() {
    const { hasError, error } = this.state;
    if (!hasError) {
      return this.props.children;
    }

    const { message = '', stack = '' } = error || {};
    const customFallback = this.props.renderFallback?.({ message, error });

    return customFallback ?? <Fallback message={message} stack={stack} />;
  }
}
